import axios from 'axios';
import { Role, User, Category, MenuItem, Order, Table, OrderItem } from '../types';
import config from '../config';

// Create axios instance with default config
export const api = axios.create({
    baseURL: config.apiUrl,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        if (config.baseURL && config.url) {
            console.log('Request URL:', config.baseURL + config.url);
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add response interceptor to handle common errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        // Only redirect on 401 errors that aren't from the login endpoint
        if (error.response?.status === 401 && !error.config.url?.includes('/auth/login')) {
            // Handle unauthorized access
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Helper function to validate order status
const validateOrderStatus = (status: string): Order['status'] => {
    const validStatuses: Order['status'][] = ['pending', 'preparing', 'ready', 'served', 'cancelled'];
    return validStatuses.includes(status as Order['status']) ? status as Order['status'] : 'pending';
};

// User Management APIs
export const userApi = {
    getUsers: async (): Promise<User[]> => {
        const response = await api.get('/users');
        return response.data;
    },

    createUser: async (userData: { username: string; pin: string; role: string }): Promise<User> => {
        const response = await api.post('/users', userData);
        return response.data;
    },

    updateUserRole: async (userId: number, role: string): Promise<void> => {
        await api.put(`/users/${userId}/role`, { role });
    },

    resetUserPin: async (userId: number, newPin: string): Promise<void> => {
        await api.put(`/users/${userId}/pin`, { pin: newPin });
    },

    deleteUser: async (userId: number): Promise<void> => {
        await api.delete(`/users/${userId}`);
    }
};

// Role Management APIs
export const roleApi = {
    getRoles: async (): Promise<Role[]> => {
        const response = await api.get('/roles');
        return response.data;
    }
};

// Menu Management APIs
export const menuApi = {
    getCategories: async (): Promise<Category[]> => {
        const response = await api.get('/menu/categories');
        return response.data;
    },

    createCategory: async (categoryData: { name: string; description?: string }): Promise<Category> => {
        const response = await api.post('/menu/categories', categoryData);
        return response.data;
    },

    updateCategory: async (categoryId: number, categoryData: { name: string; description?: string }): Promise<void> => {
        await api.put(`/menu/categories/${categoryId}`, categoryData);
    },

    getMenuItems: async (): Promise<MenuItem[]> => {
        const response = await api.get('/menu/items');
        return response.data;
    },

    createMenuItem: async (itemData: { name: string; price: number; category_id: number; description?: string; image_url?: string }): Promise<MenuItem> => {
        const response = await api.post('/menu/items', itemData);
        return response.data;
    },

    updateMenuItem: async (itemId: number, itemData: { name: string; price: number; category_id: number; description?: string; image_url?: string }): Promise<void> => {
        await api.put(`/menu/items/${itemId}`, itemData);
    },

    toggleItemAvailability: async (itemId: number, isAvailable: boolean): Promise<void> => {
        await api.patch(`/menu/items/${itemId}/availability`, { is_available: isAvailable });
    },

    getMenuItem: async (itemId: number): Promise<MenuItem> => {
        const response = await api.get(`/menu/items/${itemId}`);
        return response.data;
    }
};

// Order Management APIs
export const orderApi = {
    getOrders: async (): Promise<Order[]> => {
        const response = await api.get('/orders');
        return response.data;
    },

    getOrderDetails: async (orderId: number): Promise<Order> => {
        const response = await api.get(`/orders/${orderId}`);
        return response.data;
    },

    getOrderItems: async (orderId: number): Promise<OrderItem[]> => {
        const response = await api.get(`/orders/${orderId}/items`);
        return response.data;
    },

    createOrder: async (orderData: { 
        table_number: number; 
        items: Array<{ 
            menu_item_id: number; 
            quantity: number; 
            notes?: string 
        }>;
        total_amount: number;
    }): Promise<Order> => {
        try {
            const response = await api.post('/orders', orderData);
            return response.data;
        } catch (error: any) {
            if (error.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            throw error;
        }
    },

    updateOrder: async (orderId: number, orderData: {
        table_number: number;
        items: Array<{
            menu_item_id: number;
            quantity: number;
            notes?: string;
        }>;
        total_amount: number;
    }): Promise<Order> => {
        try {
            const response = await api.put(`/orders/${orderId}`, orderData);
            return response.data;
        } catch (error: any) {
            if (error.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            throw error;
        }
    },

    updateOrderStatus: async (orderId: number, status: Order['status']): Promise<Order> => {
        const response = await api.patch(`/orders/${orderId}/status`, { status });
        return response.data;
    },

    deleteOrder: async (orderId: number): Promise<void> => {
        await api.delete(`/orders/${orderId}`);
    }
};

// Sales Management APIs
export const salesApi = {
    getSales: async (): Promise<{ total: number; today: number; recent: Array<{ id: number; amount: number; date: string }> }> => {
        try {
            const response = await api.get('/sales');
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                // If it's a 404, return default values instead of throwing
                if (error.response?.status === 404) {
                    return {
                        total: 0,
                        today: 0,
                        recent: []
                    };
                }
            }
            throw error;
        }
    }
};

// Table Management APIs
export const tableApi = {
    getTables: async (): Promise<Table[]> => {
        const response = await api.get('/tables');
        return response.data;
    },

    createTable: async (tableData: { number: number }): Promise<Table> => {
        const response = await api.post('/tables', tableData);
        return response.data;
    },

    updateTableStatus: async (tableId: number, status: Table['status']): Promise<void> => {
        await api.patch(`/tables/${tableId}/status`, { status });
    },

    deleteTable: async (tableId: number): Promise<void> => {
        await api.delete(`/tables/${tableId}`);
    }
}; 